import classnames from 'classnames'
import ContactFilterBuilder from 'containers/ContactFilterBuilder'
import { pluralize } from 'humanize-plus'
import { isEmpty } from 'ramda'
import React, 
{ Component } from 'react'

import { EmptyState } from '@driftt/crm-components'
import {
  Button,
  Flex 
} from '@driftt/dds'
import { Table } from '@driftt/dds'
import {
  Modal,
  NavigationHeader,
} from '@driftt/tide-core'
import { tracking } from '@driftt/traffic-control'
import {
  archiveContactGroup,
  searchContactList 
} from 'api/contacts'
import {
  getFilteredContacts,
  searchContacts 
} from 'api/segments'
import ContactControls from 'components/Contacts/ContactControls'
import { getColumns } from 'components/Contacts/ContactList/constants'
import CreateNewContactSidebar from 'components/Contacts/CreateNewContactSidebar'
import Paginator from 'components/Paginator'

import ErrorBoundaryWithSentry from '../../utils/sentry/SentryErrorBoundary'
import BulkActionButtons from './ContactControls/BulkActionButtons'
import ManageContacts from './ManageContacts'

import './style.css'

const WAIT_INTERVAL = 700

const TABLE_EVENTS = Object.freeze({
  PAGINATION: 'pagination',
  ROW_SELECTION: 'rowSelection',
  SORTING: 'sorting',
})

class ContactListContainer extends Component {
  constructor(props) {
    super(props)

    this.state = {
      contactsLoading: true,
      size: 25,
      index: 0,
      totalPages: null,
      currentPage: 0,
      isLoading: true,
      isFirstLoad: true,
      createNewContact: false,
      segmentationSidebarOpen: props.location.search ? true : false,
      showDeleteConfirmationModal: false,
      isSorted: false,
      isSearching: false,
      currentFilter: {},
      sort: {
        direction: 'asc',
        property: 'name' 
      },
      totalContacts: 0,
      contactId: props.match.params ? props.match.params.id : null
    }

    this.onSearch.bind(this)
    this.memoizedColumns = null
    this.memoizedOnDelete = null
  }

  getColumnsForTable = () => {
    if (!this.memoizedColumns || this.memoizedOnDelete !== this.onDelete) {
      this.memoizedOnDelete = this.onDelete
      this.memoizedColumns = getColumns(this.onDelete)
    }
    return this.memoizedColumns
  }

  handleTableStateChange = (action) => {
    const {
      event,
      sorting 
    } = action
    if (event === TABLE_EVENTS.SORTING) {
      const newSort = sorting?.[sorting?.length - 1]
      if (newSort) {
        const { sort } = this.state
        const shouldToggleDirection = newSort.id === sort.property
        let sortDirection = newSort.desc ? 'desc' : 'asc'

        if (shouldToggleDirection) {
          sortDirection = sort.dir === 'asc' ? 'desc' : 'asc'
        }

        this.setState({
          sort: {
            dir: sortDirection,
            property: newSort.id
          },
          isSorted: true,
          isLoading: true
        }, this.searchContacts)
      }
    }
    if (event === TABLE_EVENTS.ROW_SELECTION) {
      this.props.onSelectContact(Object.keys(action?.rowSelection || {}))
    }
  }

  componentWillMount() {
    this.timer = null
  }

  previousPage = () => {
    const {
      size,
      index 
    } = this.state

    const newIndex = index - size

    this.setState(prevState => ({
      currentPage: prevState.currentPage - 1,
      index: newIndex,
      isLoading: true
    }), this.contextualGet)
  }

  nextPage = () => {
    const {
      size,
      index 
    } = this.state

    const newIndex = index + size

    this.setState(prevState => {
      const nextPage = prevState.currentPage + 1
      return {
        currentPage: nextPage,
        index: newIndex,
        isLoading: true
      }
    }, this.contextualGet)
  }

  changeSize = ({ value }) => {
    this.setState({
      size: value,
      isLoading: true
    }, this.contextualGet)
  }

  componentDidMount() {
    tracking.trackEvent('Contact List - Page Viewed')

    this.getContacts()
  }

  getContacts = async () => {
    const { onReceiveContacts } = this.props

    const {
      index,
      size 
    } = this.state

    try {
      const { data } = await searchContactList(size, index)

      onReceiveContacts(data.hydratedSegmentResults)

      this.setState({
        totalContacts: data.total,
        totalPages: Math.ceil(data.total / size),
        isFirstLoad: false,
        isLoading: false
      })

    } catch (error) {
      console.log(error)
    }
  }

  getFilteredContacts = async () => {
    const {
      currentFilter,
      index,
      size 
    } = this.state

    const { onReceiveContacts } = this.props

    try {
      const { data } = await getFilteredContacts(currentFilter, size, index)

      onReceiveContacts(data.hydratedSegmentResults)

      this.setState({
        totalContacts: data.total,
        totalPages: Math.ceil(data.total / size),
        isLoading: false
      })
    } catch(e) {
      console.log(e)
    }
  }

  searchContacts = async () => {
    const {
      currentFilter,
      index,
      size,
      sort: {
        dir,
        property 
      }
    } = this.state
    const { onReceiveContacts } = this.props

    const query = isEmpty(currentFilter) ? null : currentFilter
    const sortAscended = dir === 'asc' ? true : false

    try {
      const { data } = await searchContacts({
        query,
        size,
        sortAscended,
        sortField: property,
        startFrom: index 
      })

      onReceiveContacts(data.hydratedSegmentResults)

      this.setState({
        totalContacts: data.total,
        totalPages: Math.ceil(data.total / size),
        isLoading: false
      })
    } catch(e) {
      console.log(e)
    }
  }

  contextualGet = () => {
    const {
      currentFilter,
      isSorted 
    } = this.state

    if (isSorted || !isEmpty(currentFilter)) {
      this.searchContacts()
    } else {
      this.getContacts()
    }
  }

  setStickyHeader = (event) => {
    const { hasScrolled } = this.state

    const scrollingElement = event.target

    const offset = scrollingElement.scrollTop

    if (!hasScrolled && offset > 20) {
      this.setState({ hasScrolled: true })
    } else if (hasScrolled && offset === 0) {
      this.setState({ hasScrolled: false })
    }
  }

  resetTableRowSelection = () => {
    if (this.tableRef) {
      this.tableRef.resetRowSelection()
    }
  }

  toggleDeleteModal = () => {
    const {
      onDeselectContact,
      selectedContacts 
    } = this.props

    this.setState(prevState => ({ showDeleteConfirmationModal: !prevState.showDeleteConfirmationModal, }), () => {
      this.resetTableRowSelection()
      onDeselectContact(selectedContacts)
    })
  }



  toggleSegmentBuilderSidebar = () => {
    tracking.trackEvent('Contact List - Filter Builder Viewed')

    this.setState(prevState => ({ segmentationSidebarOpen: !prevState.segmentationSidebarOpen }))
  }

  toggleNewContactSidebar = () => {
    this.setState(prevState => ({ createNewContact: !prevState.createNewContact }))
  }

  onDelete = (selectedContact) => {
    const { onSelectContact } = this.props

    if (selectedContact) {
      onSelectContact(String(selectedContact))
    }

    this.setState({ showDeleteConfirmationModal: true })
  }

  deleteContacts = async () => {
    const {
      selectedContacts,
      showTransientNotification,
      onDeselectContact 
    } = this.props
    
    try {
      await archiveContactGroup(selectedContacts)

      onDeselectContact(selectedContacts)

      showTransientNotification({
        type: 'success',
        message: 'Successfully deleted contacts' 
      })
      
      this.setState({
        showDeleteConfirmationModal: false,
        isLoading: true,
      }, () => {
        this.resetTableRowSelection()
        this.getContacts()
      })
    } catch(e) {
      console.log(e)
      showTransientNotification({
        type: 'error',
        message: 'Sorry, an issue has occurred' 
      })
    }
  }

  handleSegmentSave = async (response) => {
    const { history } = this.props

    history.push(`/segments/${response.userListId}`)
  }

  handleSearchEntry = (event) => {
    clearTimeout(this.timer)

    this.setState({
      currentSearchTerm: event.target.value,
      isLoading: true
    })

    this.timer = setTimeout(this.onSearch, WAIT_INTERVAL)
  }

  onSearch = () => {
    const { currentSearchTerm } = this.state

    if (currentSearchTerm === '') {
      this.setState({
        currentFilter: {},
        isSearching: false,
        isLoading: true
      }, this.getContacts)
    } else {
      this.setState({
        currentFilter: currentSearchTerm,
        isSearching: true,
        isLoading: true,
        index: 0,
        currentPage: 0
      }, this.searchContacts)
    }
  }

  updateNewQuery = (query) => {
    this.setState({
      currentFilter: query,
      isLoading: true,
      isFiltering: true
    }, this.getFilteredContacts)
  }

  render() {
    const { 
      totalPages, 
      size, 
      currentPage, 
      isLoading, 
      isSearching,
      isFiltering,
      isFirstLoad, 
      segmentationSidebarOpen,
      totalContacts,
      createNewContact,
      hasScrolled,
      showDeleteConfirmationModal
    } = this.state
    
    const {
      contacts,
      history,
      selectedContacts 
    } = this.props

    return (
      <ErrorBoundaryWithSentry>
        <NavigationHeader title="All contacts">
          <ManageContacts
            addNew={this.toggleNewContactSidebar}
            resetContacts={this.getContacts}
            allContacts={totalContacts}
          />
        </NavigationHeader>
        <div className="contacts-container">
          <ContactControls
            totalContacts={totalContacts}
            onChange={this.handleSearchEntry}
            showSegmentation={this.toggleSegmentBuilderSidebar}
          />
          <div
            className={classnames('contacts-list-table-container', { 'contacts-list-table--stick': hasScrolled })}
            onScroll={this.setStickyHeader}
          >
            {!!contacts &&
              <Table
                tableRef={ref => this.tableRef = ref}
                columns={this.getColumnsForTable()}
                rows={contacts}
                isLoading={isLoading}
                enableMultiRowSelection
                enableRowSelection
                childrenBannerActions={
                  <BulkActionButtons
                    deleteContacts={this.onDelete}
                  />
                }
                onStateChange={this.handleTableStateChange}
              />
            }
            {(contacts.length === 0 && !isFirstLoad && !isLoading && (isSearching || isFiltering)) &&
              <EmptyState topText="We couldn’t find any contacts matching your filters" bottomText="Try broadening your filters or search terms to see results here." />
            }
            {(contacts.length === 0 && !isFirstLoad && !isLoading && !isSearching && !isFiltering) &&
              <EmptyState history={history} topText="You currently have no contacts."
                bottomText="Click below to upload your contacts CSV" ctaUrl="/imports/upload"
                ctaText="Upload a CSV" />
            }
            {totalContacts === 0 || (!!contacts &&
              <Paginator
                totalPages={totalPages}
                size={size}
                goBackward={this.previousPage}
                goForward={this.nextPage}
                changeSize={this.changeSize}
                index={currentPage}
              />)
            }
            {!!createNewContact &&
              <CreateNewContactSidebar
                close={this.toggleNewContactSidebar}
                onSave={this.createNewContact}
                open={createNewContact}
              />
            }
            <ContactFilterBuilder
              open={segmentationSidebarOpen}
              close={this.toggleSegmentBuilderSidebar}
              onSave={this.handleSegmentSave}
              onUpdate={this.updateNewQuery}
            />
            <Modal
              open={showDeleteConfirmationModal}
              type="warning"
              size="small"
              title="Confirm contact deletion"
              className="contacts-list-delete-confirmation-modal"
              onClose={this.toggleDeleteModal}
            >
              <div className="contacts-list-delete-confirmation">
                <p>
                  {`You are about to delete ${selectedContacts.length} ${pluralize(selectedContacts.length, 'contact')}. This action cannot be undone.`}
                </p>
              </div>
              <Flex justifyContent="flex-end">
                <Button variant="secondary" onClick={this.toggleDeleteModal}>
                  Cancel
                </Button>
                <Button variant="error" onClick={this.deleteContacts}>
                  Confirm
                </Button>
              </Flex>
            </Modal>
          </div>
        </div>
      </ErrorBoundaryWithSentry>
    )
  }
}

export default ContactListContainer
